import React from 'react';
import type { AttentionItem } from '../../domain/types';
import {
  BrandIcon,
  Badge,
  CardExpandToggle,
  ViewContextButton,
  ViewDetailButton,
} from '../../ui/common';

interface GmailCardHeaderProps {
  item: AttentionItem;
  isExpanded: boolean;
  messageCount: number;
  gmailWebUrl: string;
  onMarkRead: (e: React.MouseEvent) => void;
  onOpenContext: (e: React.MouseEvent) => void;
  onOpenDetail: (e: React.MouseEvent) => void;
  onToggleExpanded: (e: React.MouseEvent) => void;
}

/** Collapsed-card identity, signals, and action row — extracted from
 * GmailCard to keep that component's complexity within the project's CRAP
 * gate as this card gained item-summary, external-open, and draft actions. */
export const GmailCardHeader: React.FC<GmailCardHeaderProps> = ({
  item,
  isExpanded,
  messageCount,
  gmailWebUrl,
  onMarkRead,
  onOpenContext,
  onOpenDetail,
  onToggleExpanded,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8, flexWrap: 'wrap' }}>
    <BrandIcon source="gmail" size={15} />
    <span
      style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--z-muted-2)',
      }}
    >
      {item.fromName || item.primary}
    </span>
    <Badge label={item.kind || 'Correo'} tone="flat" />
    {messageCount > 1 && <Badge label={`${messageCount} mensajes`} tone="flat" />}
    {item.unread && <Badge label="No leído" tone="warn" />}
    {item.blocksWork && <Badge label="Bloquea trabajo" tone="bad" />}
    {item.unread && (
      <button
        type="button"
        data-testid="gmail-mark-read"
        onClick={onMarkRead}
        style={{
          border: 'none',
          background: 'var(--z-fill)',
          color: 'var(--z-muted-2)',
          borderRadius: 999,
          padding: '4px 10px',
          fontSize: 11.5,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Marcar como leído
      </button>
    )}
    <ViewContextButton onClick={onOpenContext} />
    <ViewDetailButton onClick={onOpenDetail} />
    {gmailWebUrl ? (
      <a
        href={gmailWebUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          height: 28,
          padding: '0 10px',
          borderRadius: 9,
          border: '1px solid var(--z-border)',
          background: 'var(--z-surface)',
          color: 'var(--z-muted-2)',
          fontSize: 11.5,
          fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        <span>Abrir en Gmail</span>
        <span style={{ fontSize: 10 }}>↗</span>
      </a>
    ) : null}
    <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--z-dim)' }}>{item.age}</span>
    <CardExpandToggle
      isExpanded={isExpanded}
      onClick={onToggleExpanded}
      ariaLabel={isExpanded ? 'Contraer correo' : 'Expandir correo'}
    />
  </div>
);
